import React from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';

// Paleta FMP
const CORES = {
  recebimentos: '#1fa971',  // verde
  transfEntrada: '#00b5cc', // cyan
  pagamentos: '#e5484d',    // vermelho
  transfSaida: '#f1be48',   // dourado
  saldo: '#00205b',         // navy
  hoje: '#e5484d',
};

const fmt = (v) => {
  if (!v && v !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(v));
};

const fmtAxis = (v) => {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1).replace('.', ',')}M`;
  if (abs >= 1_000) return `R$ ${(v / 1_000).toFixed(0)}k`;
  return `R$ ${v}`;
};

const parseLabel = (d, idx) => {
  if (d.data_completa) {
    const p = d.data_completa.split('/');
    if (p.length === 3) {
      const dt = new Date(Number(p[2]), parseInt(p[1]) - 1, parseInt(p[0]));
      return dt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '');
    }
  }
  return String(d.dia || idx + 1);
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload?._original;
  if (!d) return null;

  const tag = d.eh_hoje ? 'Hoje' : d.eh_futuro ? 'Previsão' : 'Realizado';
  const bars = payload.filter((p) => p.dataKey !== 'saldo' && Math.abs(p.value) > 0);

  return (
    <div style={{
      background: '#0e1726',
      border: '1px solid rgba(146,162,196,0.18)',
      borderRadius: 12,
      padding: '12px 14px',
      minWidth: 230,
      pointerEvents: 'none',
      boxShadow: '0 16px 34px -10px rgba(4,18,44,0.6)',
    }}>
      <div style={{ color: '#9bacca', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <span>{d.dia_semana ? `${d.dia_semana} · ` : ''}{d.data_completa}</span>
        <span style={{ color: d.eh_hoje ? '#f1be48' : '#7d8aa3', fontWeight: 700 }}>{tag}</span>
      </div>
      {bars.length ? bars.map((p) => (
        <div key={p.dataKey} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', color: '#dde6f6' }}>
          <span style={{ width: 9, height: 9, background: p.fill, display: 'inline-block', borderRadius: '50%', flexShrink: 0 }} />
          <span style={{ flex: 1 }}>{p.name}</span>
          <b style={{ color: '#fff', fontVariantNumeric: 'tabular-nums' }}>{fmt(Math.abs(p.value))}</b>
        </div>
      )) : (
        <div style={{ fontSize: 12.5, color: '#7d8aa3', padding: '2px 0' }}>Sem movimento no dia</div>
      )}
      <div style={{ marginTop: 8, borderTop: '1px solid rgba(146,162,196,0.22)', paddingTop: 8, fontSize: 13, color: '#9bacca', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Saldo projetado</span>
        <b style={{ color: '#fff', fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{fmt(d.saldo_acumulado)}</b>
      </div>
    </div>
  );
};

const CustomDot = (props) => {
  const { cx, cy, payload } = props;
  if (!cx || !cy) return null;
  const isHoje = payload?._original?.eh_hoje;
  if (isHoje) {
    return (
      <g>
        <circle cx={cx} cy={cy} r={9} fill={CORES.hoje} opacity={0.18} />
        <circle cx={cx} cy={cy} r={5} fill={CORES.hoje} stroke="#fff" strokeWidth={2} />
      </g>
    );
  }
  return <circle cx={cx} cy={cy} r={3} fill="#fff" stroke={CORES.saldo} strokeWidth={1.8} />;
};

const FluxoCaixaDiarioChart = ({ dados = [], onClickDia }) => {
  if (!dados.length) return null;

  const data = dados.map((d, i) => ({
    label: parseLabel(d, i),
    recebimentos: d.entradas || 0,
    transfEntrada: d.transferencias_entrada || 0,
    pagamentos: -(d.saidas || 0),
    transfSaida: -(d.transferencias_saida || 0),
    saldo: d.saldo_acumulado,
    _original: d,
  }));

  const todayLabel = data.find((d) => d._original?.eh_hoje)?.label;

  const handleBarClick = (barProps) => {
    const d = barProps?.payload?._original ?? barProps?._original;
    if (d && onClickDia) onClickDia(d);
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={data}
        stackOffset="sign"
        margin={{ top: 24, right: 16, left: 8, bottom: 70 }}
        style={{ cursor: 'pointer' }}
      >
        <defs>
          <linearGradient id="gradSaldoFluxo" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={CORES.saldo} stopOpacity={0.12} />
            <stop offset="100%" stopColor={CORES.saldo} stopOpacity={0} />
          </linearGradient>
        </defs>

        <CartesianGrid strokeDasharray="4 4" stroke="#eef1f6" vertical={false} />

        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: '#98a4b8' }}
          angle={-45}
          textAnchor="end"
          interval={0}
          tickLine={false}
          axisLine={false}
          dy={6}
        />
        <YAxis
          tickFormatter={fmtAxis}
          tick={{ fontSize: 11, fill: '#98a4b8' }}
          tickLine={false}
          axisLine={false}
          width={62}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,32,91,0.06)' }} />
        <Legend
          verticalAlign="top"
          align="right"
          wrapperStyle={{ fontSize: 12, color: '#475467', paddingBottom: 10 }}
          iconType="circle"
          iconSize={9}
        />

        {/* Linha zero */}
        <ReferenceLine y={0} stroke="#cbd5e1" strokeWidth={1} />

        {/* Linha "Hoje" */}
        {todayLabel && (
          <ReferenceLine
            x={todayLabel}
            stroke={CORES.hoje}
            strokeDasharray="5 5"
            strokeWidth={1.5}
            label={{ value: 'Hoje', position: 'insideTopRight', fill: CORES.hoje, fontSize: 10, fontWeight: 700 }}
          />
        )}

        {/* Área sutil sob o saldo (sensação de "nível de caixa") */}
        <Line
          dataKey="saldo"
          type="monotone"
          stroke="none"
          fill="url(#gradSaldoFluxo)"
          legendType="none"
          dot={false}
          activeDot={false}
          isAnimationActive={false}
        />

        {/* Barras empilhadas por sinal (positivas acima, negativas abaixo do zero) */}
        <Bar dataKey="recebimentos" name="Recebimentos" stackId="s" fill={CORES.recebimentos} radius={[0, 0, 0, 0]} maxBarSize={42} onClick={handleBarClick} cursor="pointer" />
        <Bar dataKey="transfEntrada" name="Transf. Entrada" stackId="s" fill={CORES.transfEntrada} radius={[4, 4, 0, 0]} maxBarSize={42} onClick={handleBarClick} cursor="pointer" />
        <Bar dataKey="pagamentos" name="Pagamentos" stackId="s" fill={CORES.pagamentos} radius={[0, 0, 0, 0]} maxBarSize={42} onClick={handleBarClick} cursor="pointer" />
        <Bar dataKey="transfSaida" name="Transf. Saída" stackId="s" fill={CORES.transfSaida} radius={[0, 0, 4, 4]} maxBarSize={42} onClick={handleBarClick} cursor="pointer" />

        {/* Linha de saldo acumulado */}
        <Line
          dataKey="saldo"
          name="Saldo"
          type="monotone"
          stroke={CORES.saldo}
          strokeWidth={2.5}
          dot={<CustomDot />}
          activeDot={{ r: 5, fill: CORES.saldo, stroke: '#fff', strokeWidth: 2 }}
          legendType="none"
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default FluxoCaixaDiarioChart;
